import { useEffect, useState } from 'react'
import { subscribeBroadcast } from '@highlight/ipc'
import {
  TRANSCRIPT_PARTIALS_TOPIC,
  type PartialTranscription,
  type TranscriptPartialMessage,
  type TranscriptPartialSource,
} from '@shared/transcript-feed'

export type PartialTranscriptions = Record<TranscriptPartialSource, PartialTranscription | null>

export interface LivePartial extends PartialTranscription {
  source: TranscriptPartialSource
}

const EMPTY_PARTIALS: PartialTranscriptions = { microphone: null, loopback: null }

/**
 * The partials that carry text, oldest utterance first. The last is the live
 * line; on a tie the microphone's is.
 */
export function orderLivePartials(partials: PartialTranscriptions): Array<LivePartial> {
  const live: Array<LivePartial> = []
  for (const source of ['loopback', 'microphone'] as const) {
    const partial = partials[source]
    if (partial?.text) {
      live.push({ ...partial, source })
    }
  }
  return live.sort((a, b) => a.startTimestamp - b.startTimestamp)
}

/**
 * Live partial (in-flight) transcript lines from the partials broadcast
 * topic. Only subscribed while mounted and `enabled` — an unmounted/disabled
 * consumer means this renderer receives no partial-transcript IPC at all.
 */
export function usePartialTranscriptions(enabled = true): PartialTranscriptions {
  const [partials, setPartials] = useState<PartialTranscriptions>(EMPTY_PARTIALS)

  useEffect(() => {
    if (!enabled) {
      setPartials(EMPTY_PARTIALS)
      return
    }
    return subscribeBroadcast<TranscriptPartialMessage>(TRANSCRIPT_PARTIALS_TOPIC, (message) => {
      setPartials((prev) =>
        prev[message.source] === message.partial ? prev : { ...prev, [message.source]: message.partial },
      )
    })
  }, [enabled])

  return partials
}
